import { useLocation } from 'wouter';
import {
    CheckCircle2,
    Clock,
    Settings,
    ArrowRight,
    RefreshCw,
} from 'lucide-react';
import MarketingNav from '../components/marketing/MarketingNav';
import MarketingFooter from '../components/marketing/MarketingFooter';
import { usePageSEO } from '../utils/seo';

const HowItWorksPage = () => {
    const [, setLocation] = useLocation();

    usePageSEO({
        title: 'How BayWhiz Works — End-to-End Dock Scheduling Workflow',
        description:
            'See how BayWhiz handles warehouse dock scheduling from carrier PO validation and slot requests through warehouse approval, dock door assignment, check-in, and dwell tracking.',
        canonicalPath: '/how-it-works',
    });

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-teal-500/20 font-sans overflow-x-hidden">
            <MarketingNav variant="light" />

            {/* 1. HERO */}
            <section className="pt-16 sm:pt-20 pb-16 px-4 sm:px-6 text-center max-w-4xl mx-auto">
                <span className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-4 block">
                    How BayWhiz Works
                </span>
                <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold mb-6 text-slate-900 tracking-tight leading-tight">
                    From appointment request to dock door &mdash; one shared workflow.
                </h1>
                <p className="text-base sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-10">
                    BayWhiz gives carriers a structured way to request appointments against valid POs and gives your warehouse one place to review, assign dock doors, and run the shift.
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
                    <button
                        type="button"
                        onClick={() => setLocation('/request-trial')}
                        className="w-full sm:w-auto h-14 px-8 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-base transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer"
                    >
                        Request Free Trial
                        <ArrowRight size={18} />
                    </button>
                    <button
                        type="button"
                        onClick={() => setLocation('/product')}
                        className="w-full sm:w-auto h-14 px-8 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold text-base transition-all cursor-pointer"
                    >
                        Explore the Product
                    </button>
                </div>
                <p className="text-xs sm:text-sm text-slate-500 mt-5 font-medium">
                    30-day full-access trial &middot; No credit card required
                </p>
            </section>

            {/* 2. HIGH-LEVEL WORKFLOW */}
            <section className="py-10 px-4 sm:px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 relative">
                        <div className="hidden md:block absolute top-[28px] left-[8%] right-[8%] h-[2px] bg-slate-200 z-0" />

                        {[
                            { step: 1, title: 'Set up your docks' },
                            { step: 2, title: 'Share booking link' },
                            { step: 3, title: 'Carrier requests time' },
                            { step: 4, title: 'Warehouse reviews' },
                            { step: 5, title: 'Assign dock door' },
                            { step: 6, title: 'Track check-in & dwell' },
                        ].map((item) => (
                            <div
                                key={item.step}
                                className="relative z-10 flex flex-row md:flex-col items-center gap-4 md:gap-0 md:text-center bg-white md:bg-transparent p-4 md:p-0 rounded-xl border border-slate-200 md:border-none shadow-sm md:shadow-none"
                            >
                                <div className="w-14 h-14 rounded-full bg-white border-2 border-teal-200 text-teal-700 font-bold flex items-center justify-center md:mb-4 shrink-0 shadow-sm">
                                    {item.step}
                                </div>
                                <h2 className="font-bold text-slate-800 text-[15px] leading-tight">{item.title}</h2>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 3. STEP 1 — SET UP THE WAREHOUSE */}
            <section className="py-20 px-4 sm:px-6 bg-white border-t border-slate-200">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="order-2 md:order-1 bg-slate-50 rounded-2xl p-8 border border-slate-200 flex flex-col justify-center items-center">
                            <div className="w-full max-w-sm space-y-3">
                                <div className="bg-white border border-slate-200 p-3.5 rounded-xl shadow-sm flex items-center justify-between">
                                    <span className="text-sm font-medium text-slate-700">Facility Timezone</span>
                                    <span className="text-sm font-bold text-slate-900">America/Vancouver</span>
                                </div>
                                <div className="bg-white border border-slate-200 p-3.5 rounded-xl shadow-sm flex items-center justify-between">
                                    <span className="text-sm font-medium text-slate-700">Dock Doors Configured</span>
                                    <span className="text-sm font-bold text-slate-900">Docks D1 &ndash; D4</span>
                                </div>
                                <div className="bg-white border border-slate-200 p-3.5 rounded-xl shadow-sm flex items-center justify-between">
                                    <span className="text-sm font-medium text-slate-700">Receiving Hours</span>
                                    <span className="text-sm font-bold text-slate-900">06:00 &ndash; 20:00</span>
                                </div>
                                <div className="bg-white border border-slate-200 p-3.5 rounded-xl shadow-sm flex items-center justify-between">
                                    <span className="text-sm font-medium text-slate-700">Purchase Orders</span>
                                    <span className="text-sm font-bold text-teal-700">Manual Entry or CSV Import</span>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 md:order-2">
                            <div className="text-teal-700 font-bold mb-2 tracking-wide uppercase text-xs">Step 1</div>
                            <h2 className="text-2xl sm:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                                Configure your facility, docks, and purchase orders.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 mb-6 leading-relaxed">
                                Define your facility timezone, dock doors, equipment capabilities, operating hours, and slot duration to establish the scheduling capacity carriers can book against.
                            </p>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-medium">
                                Add Purchase Orders directly or import them via CSV. No WMS replacement or complex integration project is required to start scheduling.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. STEP 2 — CARRIER PATH */}
            <section className="py-20 px-4 sm:px-6 bg-slate-50 border-t border-slate-200">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div>
                            <div className="text-teal-700 font-bold mb-2 tracking-wide uppercase text-xs">Step 2</div>
                            <h2 className="text-2xl sm:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                                Share one booking link with your carriers.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 mb-6 leading-relaxed">
                                Share your facility&rsquo;s public booking link with carriers and freight brokers. Carriers validate their PO or load number first, select an open date and time window, and submit their MC number, driver, truck, and trailer details.
                            </p>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-medium">
                                Carriers can submit requests as guests or claim bookings into a free carrier account using a signed claim token.
                            </p>
                        </div>
                        <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white">
                            <img
                                src="/landing/real-carrier-booking.png"
                                alt="BayWhiz carrier self-service appointment request portal"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. STEP 3 — REVIEW */}
            <section className="py-20 px-4 sm:px-6 bg-white border-t border-slate-200">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="order-2 md:order-1 rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white">
                            <img
                                src="/landing/real-appointment-approval.png"
                                alt="BayWhiz Awaiting Approval queue with Approve, Revise, and Decline actions"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                        <div className="order-1 md:order-2">
                            <div className="text-teal-700 font-bold mb-2 tracking-wide uppercase text-xs">Step 3</div>
                            <h2 className="text-2xl sm:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                                Your warehouse stays in control of every booking.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 mb-6 leading-relaxed">
                                Carrier requests enter your <strong>Awaiting Approval</strong> queue rather than overwriting your dock board automatically. Your receiving team verifies the PO, pallet count, and dock capacity.
                            </p>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-medium">
                                Approve the request onto an available dock door, revise the appointment time, or decline with a documented reason.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 6. STEP 4 — LIVE SCHEDULE */}
            <section className="py-20 px-4 sm:px-6 bg-slate-50 border-t border-slate-200">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div>
                            <div className="text-teal-700 font-bold mb-2 tracking-wide uppercase text-xs">Step 4</div>
                            <h2 className="text-2xl sm:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                                Approved appointments appear on your live dock timeline.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 mb-6 leading-relaxed">
                                Once approved, the appointment is placed onto its assigned dock door on the live schedule with all carrier, equipment, and linked PO context ready for the shift.
                            </p>
                            <ul className="space-y-4 mb-6">
                                {[
                                    'Scheduled time window in your facility local timezone',
                                    'Assigned dock door (with one-click dock reassignment)',
                                    'Carrier company, MC #, driver name, truck #, and trailer #',
                                    'Linked PO numbers and expected pallet counts',
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-slate-700">
                                        <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center shrink-0 mt-0.5">
                                            <div className="w-2 h-2 rounded-full bg-teal-600" />
                                        </div>
                                        <span className="font-medium text-[15px]">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white">
                            <img
                                src="/landing/real-warehouse-schedule.png"
                                alt="BayWhiz Live Warehouse Schedule Timeline"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 7. STEP 5 — RUN THE SHIFT */}
            <section className="py-20 px-4 sm:px-6 bg-white border-t border-slate-200">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-14 max-w-3xl mx-auto">
                        <div className="text-teal-700 font-bold mb-2 tracking-wide uppercase text-xs">Step 5</div>
                        <h2 className="text-2xl sm:text-4xl font-bold mb-4 text-slate-900 leading-tight">
                            Run check-in, dock dispatch, and checkout from the same schedule.
                        </h2>
                        <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                            See which trucks have arrived at the gate, which dock doors are actively unloading, and reconcile PO quantities before checkout.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 mb-12">
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 flex flex-col h-full">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-3 h-3 rounded-full bg-blue-500" />
                                <h3 className="font-bold text-slate-900">Arrived &amp; Checked In</h3>
                            </div>
                            <p className="text-slate-600 text-sm leading-relaxed">
                                Record truck arrival and gate check-in. Dwell time tracking starts automatically from check-in.
                            </p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 flex flex-col h-full">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-3 h-3 rounded-full bg-amber-500" />
                                <h3 className="font-bold text-slate-900">Dispatched to Dock</h3>
                            </div>
                            <p className="text-slate-600 text-sm leading-relaxed">
                                Dispatch the checked-in truck to its assigned door and verify PO receiving counts during unloading.
                            </p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 flex flex-col h-full">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                                <h3 className="font-bold text-slate-900">Completed &amp; Logged</h3>
                            </div>
                            <p className="text-slate-600 text-sm leading-relaxed">
                                Complete the appointment to release the dock door and record final turnaround duration in Dwell Analytics.
                            </p>
                        </div>
                    </div>

                    <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white">
                        <img
                            src="/landing/real-checkin-checkout.png"
                            alt="BayWhiz Checked-In appointment view with dock dispatch and receiving reconciliation"
                            className="w-full h-auto block"
                            loading="lazy"
                        />
                    </div>
                </div>
            </section>

            {/* 8. STEP 6 — WHEN PLANS CHANGE */}
            <section className="py-20 px-4 sm:px-6 bg-slate-50 border-t border-slate-200">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="order-2 md:order-1 rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white">
                            <img
                                src="/landing/real-dock-assignment.png"
                                alt="BayWhiz Reassign Dock picker inside the appointment modal"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                        <div className="order-1 md:order-2">
                            <div className="text-teal-700 font-bold mb-2 tracking-wide uppercase text-xs">Step 6</div>
                            <h2 className="text-2xl sm:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                                Update the schedule when reality changes.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 mb-6 leading-relaxed">
                                Trucks run late, doors need maintenance, and carriers reschedule. Update dock assignments or block out dock time directly on the live schedule while preserving a complete audit trail.
                            </p>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3">
                                    <div className="w-7 h-7 rounded-full bg-red-50 text-red-600 flex items-center justify-center shrink-0 border border-red-100 mt-0.5">
                                        <Clock size={15} />
                                    </div>
                                    <span className="text-sm text-slate-700">
                                        <strong>Late Arrival Visibility:</strong> Identify overdue arrivals and active dwell time on the board.
                                    </span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <div className="w-7 h-7 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center shrink-0 border border-teal-200 mt-0.5">
                                        <RefreshCw size={15} />
                                    </div>
                                    <span className="text-sm text-slate-700">
                                        <strong>One-Click Dock Reassignment:</strong> Move an appointment to another open door with automatic conflict checking.
                                    </span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <div className="w-7 h-7 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center shrink-0 border border-amber-200 mt-0.5">
                                        <Settings size={15} />
                                    </div>
                                    <span className="text-sm text-slate-700">
                                        <strong>Block Time &amp; Maintenance Windows:</strong> Reserve dock doors for maintenance or internal staging.
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 9. OBJECTIONS / SETUP */}
            <section className="py-20 px-4 sm:px-6 bg-white border-t border-slate-200">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center mx-auto mb-6 border border-teal-200">
                        <CheckCircle2 size={24} />
                    </div>
                    <h2 className="text-2xl sm:text-4xl font-bold mb-10 text-slate-900">
                        Start without a complex warehouse software project.
                    </h2>

                    <div className="grid md:grid-cols-2 gap-4 text-left">
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex items-center gap-4">
                            <CheckCircle2 className="text-teal-600 shrink-0" size={20} />
                            <span className="font-bold text-slate-800">No WMS replacement required</span>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex items-center gap-4">
                            <CheckCircle2 className="text-teal-600 shrink-0" size={20} />
                            <span className="font-bold text-slate-800">Manual or CSV purchase order setup</span>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex items-center gap-4">
                            <CheckCircle2 className="text-teal-600 shrink-0" size={20} />
                            <span className="font-bold text-slate-800">30-day full-access trial &mdash; no credit card required</span>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex items-center gap-4">
                            <CheckCircle2 className="text-teal-600 shrink-0" size={20} />
                            <span className="font-bold text-slate-800">Free for freight carriers requesting slots</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* 10. FINAL CTA */}
            <section className="py-20 px-4 sm:px-6 bg-slate-50 border-t border-slate-200">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-5xl font-bold mb-6 text-slate-900 tracking-tight">
                        Ready to run your dock schedule in one place?
                    </h2>
                    <p className="text-base sm:text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
                        Start your 30-day full-access trial and set up your facility dock doors without replacing your existing warehouse systems.
                    </p>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
                        <button
                            type="button"
                            onClick={() => setLocation('/request-trial')}
                            className="h-14 px-8 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-base transition-all shadow-md w-full sm:w-auto flex items-center justify-center gap-2 cursor-pointer"
                        >
                            Request Trial Access
                            <ArrowRight size={18} />
                        </button>
                        <button
                            type="button"
                            onClick={() => setLocation('/pricing')}
                            className="h-14 px-8 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold text-base transition-all w-full sm:w-auto flex items-center justify-center cursor-pointer"
                        >
                            View Pricing
                        </button>
                    </div>
                    <p className="mt-5 text-xs sm:text-sm text-slate-500 font-medium">
                        30-day full-access trial &middot; No credit card required &middot; Plans start at $99 USD/month per facility
                    </p>
                </div>
            </section>

            <MarketingFooter variant="light" />
        </div>
    );
};

export default HowItWorksPage;
